import {
  Tabs,
  TabsContent,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { pageToUrl, PageFn } from '@client';
import { WizardAppConfigFeatureFlags } from '@/pages/WizardAppConfigFeatureFlags';
import { WizardFundingCalcPromptPage } from '@/pages/WizardFundingCalcPrompt';
import { WizardUnderwritingGuidelinesPage } from '@/pages/WizardUnderwritingGuidelinesPage';
import { IllustrationMonitorPage } from '@/pages/IllustrationMonitorPage';
import { WizardEvalsPage } from '@/pages/WizardEvalsPage';
import { WizardCalcSnapshotsDashboardPage } from '@/pages/WizardCalcSnapshotsPage';
import {
  FeatureFlagsForm,
  ReportingAttributesForm,
  AccountIdentifiersForm,
  CarescoutStateProbeForm,
  ClientViewerForm,
  CloneClientForm,
  EmailTemplatePreview,
  DeleteAccountForm,
  PreRegisteredFeatureFlagsForm,
  PreRegisteredTeamRelationshipsForm,
  UserSelfView,
} from '@/features/wizard-advisor-admin';
import {
  PartnerBlacklistAdmin,
  PartnerContactExclusionAdmin,
  PartnerRegistryAdmin,
} from '@/features/distribution-partners';

const DEFAULT_TAB = 'account-identifiers';

const EXTERNAL_LINK_OPTIONS: { value: string; label: string; page: PageFn<{}> }[] = [
  {
    value: 'calculation-tests',
    label: 'Calculation Tests',
    page: WizardCalcSnapshotsDashboardPage,
  },
  {
    value: 'app-config-feature-flags',
    label: 'Manage AppConfig Feature Flags',
    page: WizardAppConfigFeatureFlags,
  },
  {
    value: 'underwriting-guidelines',
    label: 'Underwriting Guidelines',
    page: WizardUnderwritingGuidelinesPage,
  },
  {
    value: 'illustration-finder',
    label: 'Illustration Finder',
    page: IllustrationMonitorPage,
  },
  {
    value: 'evals',
    label: 'AI Underwriting Evals',
    page: WizardEvalsPage,
  },
  {
    value: 'funding-calc-prompt',
    label: 'LTC Funding Calc Prompt',
    page: WizardFundingCalcPromptPage,
  },
];

const SECTION_OPTIONS: { value: string; label: string; danger?: boolean }[] = [
  { value: 'account-identifiers', label: 'Account Identifiers' },
  { value: 'user-self-view', label: 'User Self View' },
  { value: 'carescout-state-probe', label: 'CareScout State Probe' },
  { value: 'client-viewer', label: 'Client Viewer' },
  { value: 'clone-client', label: 'Clone Client' },
  { value: 'email-templates', label: 'Email Template Preview' },
  { value: 'feature-flags', label: 'Feature Flags' },
  { value: 'pre-registered-flags', label: 'Pre-Registered Flags' },
  { value: 'pre-registered-teams', label: 'Pre-Registered Teams' },
  { value: 'edj-partners', label: 'Distribution Partners' },
  { value: 'dp-blacklist', label: 'Distribution Partners Deny List' },
  { value: 'dp-contact-exclusions', label: 'Distribution Partners Exclude List' },
  { value: 'reporting-attributes', label: 'Reporting Attributes' },
  { value: 'delete-account', label: 'Delete Account', danger: true },
];

export function WizardAdvisorAdminPortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedSection = searchParams.get('tab') ?? DEFAULT_TAB;

  const onSectionChange = (value: string) => {
    const externalLink = EXTERNAL_LINK_OPTIONS.find(opt => opt.value === value);
    if (externalLink) {
      navigate(pageToUrl(externalLink.page));
      return;
    }
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('tab', value);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <main className="px-4 pt-3 pb-12 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Advisor Admin Portal"
        description="Manage advisor feature flags and reporting attributes"
      />

      <Tabs value={selectedSection}>
        <div className="mt-4 mb-6">
          <Select
            value={selectedSection}
            onValueChange={onSectionChange}
          >
            <SelectTrigger className="w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SECTION_OPTIONS.map(opt => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className={opt.danger ? 'text-red-600 focus:text-red-700' : undefined}
                >
                  {opt.label}
                </SelectItem>
              ))}
              <SelectSeparator />
              {EXTERNAL_LINK_OPTIONS.map(opt => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="feature-flags">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Manage Feature Flags</CardTitle>
              <CardDescription className="text-gray-500">
                Search for an advisor by ID or email, then manage their feature flags.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FeatureFlagsForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pre-registered-flags">
          <Card className="max-w-2xl border shadow-sm">
            <CardHeader>
              <CardTitle>Pre-Registered Feature Flags</CardTitle>
              <CardDescription className="text-muted-foreground">
                Pre-register emails so feature flags are automatically applied when the advisor
                signs up.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PreRegisteredFeatureFlagsForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pre-registered-teams">
          <PreRegisteredTeamRelationshipsForm />
        </TabsContent>

        <TabsContent value="edj-partners">
          <PartnerRegistryAdmin />
        </TabsContent>

        <TabsContent value="dp-blacklist">
          <PartnerBlacklistAdmin />
        </TabsContent>

        <TabsContent value="dp-contact-exclusions">
          <PartnerContactExclusionAdmin />
        </TabsContent>

        <TabsContent value="reporting-attributes">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Manage Advisor Attributes</CardTitle>
              <CardDescription className="text-gray-500">
                Add, update, or delete advisor reporting attributes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ReportingAttributesForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account-identifiers">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Update Account Identifiers</CardTitle>
              <CardDescription className="text-gray-500">
                Update an advisor's email address or phone number.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AccountIdentifiersForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="user-self-view">
          <Card className="max-w-3xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>User Self View</CardTitle>
              <CardDescription className="text-gray-500">
                Your current Users record and its associated Advisors record, read fresh from the
                database — useful for confirming Clerk↔DB sync state for the account you&apos;re
                acting as.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UserSelfView />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="client-viewer">
          <Card className="max-w-3xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Client Viewer</CardTitle>
              <CardDescription className="text-gray-500">
                Look up a client record by client ID.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ClientViewerForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clone-client">
          <Card className="max-w-2xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Clone Client</CardTitle>
              <CardDescription className="text-gray-500">
                Export a client + partner + all related rows as JSON, then import into another
                environment. Names are anonymized by default. Import is disabled in production.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CloneClientForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email-templates">
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>Email Template Preview</CardTitle>
              <CardDescription className="text-gray-500">
                Renders the client intake invitation exactly as the send path produces it, including
                the unsubscribe footer, with an optional legacy-template comparison.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EmailTemplatePreview />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="carescout-state-probe">
          <Card className="max-w-3xl border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle>CareScout State Probe</CardTitle>
              <CardDescription className="text-gray-500">
                Re-fire an existing CareScout quote through Genworth with a different applicant
                state to verify which filing bucket that state belongs in. Uses the real client
                record (name, age, zipcode, advisor) but substitutes the applicant state on the
                request to Genworth. Does not write to the database.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CarescoutStateProbeForm />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="delete-account">
          <Card className="max-w-2xl border border-red-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-red-700">Delete Advisor Account</CardTitle>
              <CardDescription className="text-red-500">
                Permanently delete an advisor account and all associated data. This action cannot be
                undone.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DeleteAccountForm />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}
